import { TTool, getTool } from "@highway/mbs-shared";
import {
    IAnalyticsPlatformConfig,
    IAnalyticsProvider,
    IEventDefinition,
    IUserProfile,
    TAnalyticsProvider,
    TTrackCleanupFn,
} from "../types";

const debug = process.env.NODE_ENV === "development";

export class AnalyticsManager {
    private providers: Map<TAnalyticsProvider, IAnalyticsProvider> = new Map();
    private initialized: boolean = false;
    private config: IAnalyticsPlatformConfig;
    private appEvents?: Record<string, IEventDefinition>;
    private isUserSetup: boolean = false;

    eventPrefix: string = "";

    constructor(config: IAnalyticsPlatformConfig, appEvents?: Record<string, IEventDefinition>) {
        this.config = config;
        this.appEvents = appEvents;
        this.setupProviders();
    }

    private setupProviders(): void {
        this.config.providers.forEach((provider) => {
            this.providers.set(provider.providerName, provider);
        });
    }

    async initialize(): Promise<void> {
        if (this.initialized) return;
        const initPromises = Array.from(this.providers.values()).map((provider) => provider.initialize());

        await Promise.all(initPromises);
        this.initialized = true;
    }

    setEventPrefix(prefix: string): void {
        this.eventPrefix = prefix;
    }

    track(
        eventKey: string,
        properties?: Record<string, any>,
        context?: Record<string, any>,
        cleanup?: TTrackCleanupFn,
    ): void {
        const activeTool = this.config.getActiveTool?.();
        const tool = activeTool ? getTool(activeTool) : undefined;
        const eventContext = {
            tool: tool?.tool,
            toolKey: tool?.toolKey,
            eventPrefix: this.eventPrefix || tool?.eventPrefix,
            ...(context || {}),
        };

        const eventDefinition = this.appEvents?.[eventKey];

        if (!eventDefinition) {
            if (debug) console.warn(`Event '${eventKey}' is not defined in the events registry.`);
            // return; // NOTE commenting this out and adding ? to below eventDefinition until all events are defined
        }

        // If validTools is defined, check if the eventContext.tool is valid
        if (
            eventDefinition?.validTools &&
            !eventDefinition?.validTools.includes(eventContext.tool as TTool)
        ) {
            if (debug) console.warn(`Tool '${eventContext.tool}' is not valid for event '${eventKey}'.`);
            return;
        }

        // Track the event on each enabled provider that has a mapping for this event
        this.providers.forEach((provider, providerName) => {
            const mapping = eventDefinition?.mappings[providerName];
            const finalProperties = provider.includeContextInProperties
                ? { ...properties, ...eventContext }
                : properties;

            if (mapping) {
                const eventName = typeof mapping === "function" ? mapping(eventContext) : mapping;

                // NOTE: Filter events with "undefined" prefix that fire before setEventPrefix() is called.
                // These events will re-fire with the correct prefix once the prefix is set
                if (eventName.includes("undefined")) return;
                provider.track(eventName, finalProperties, cleanup);
            } else {
                provider.track(eventKey, finalProperties, cleanup);
            }
        });
    }

    identify(userId: string): void {
        this.providers.forEach((provider) => provider.identify(userId));
    }

    setUserProfile(profile: IUserProfile): void {
        this.providers.forEach((provider) => provider.setUserProfile(profile));
    }

    setupUser(params: { userID: string; email: string; name: string; company: string }): void {
        if (!this.isUserSetup) {
            this.identify(params.userID);
            this.setUserProfile({ email: params.email, name: params.name, company: params.company });
            this.isUserSetup = true;
        }
    }

    reset(): void {
        this.providers.forEach((provider) => provider.reset());
    }
}
